import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRightIcon, ArrowLeftIcon, BuildingStorefrontIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import AccountSetupStepper from '../components/AccountSetupStepper';
import VenueDetailsStep from '../components/steps/VenueDetailsStep';
import VenueTermsStep from '../components/steps/VenueTermsStep';
import VenueSubscriptionStep from '../components/steps/VenueSubscriptionStep';
import VenueCompletionStep from '../components/steps/VenueCompletionStep';
import PtDirectoryDetailsStep from '../components/steps/PtDirectoryDetailsStep';
import PtDirectoryProfileStep from '../components/steps/PtDirectoryProfileStep';
import PaymentStep from '../components/steps/PaymentStep';
import config from '../config';
import './CreateAccount.css';

const baseUrl = config.apiBaseUrl;

// ─────────────────────────────────────────────────────────────────────────────
// VenueSignup
// Handles TWO paths through the same wizard:
//   • Venue Admin   – /signup/venue-admin?plan=xxx&voucher=xxx
//   • PT Directory  – /signup/venue-admin?plan=pt-directory-xxx  (or ?type=pt)
// ─────────────────────────────────────────────────────────────────────────────

const VENUE_STEPS   = ['Venue Details', 'Terms & Conditions', 'Subscription', 'Payment', 'Complete'];
const PT_DIR_STEPS  = ['Your Details', 'PT Profile', 'Terms & Conditions', 'Subscription', 'Payment', 'Complete'];

const VenueSignup = () => {
    const [searchParams] = useSearchParams();

    // ── determine if we start in PT-directory mode ──────────────────────────
    const forcedType = searchParams.get('type'); // 'pt' → PT directory
    const preselectedPlan = searchParams.get('plan') || '';
    const preselectedVoucher = searchParams.get('voucher') || '';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const [isPtDirectoryMode, setIsPtDirectoryMode] = useState(false);
    const [steps, setSteps] = useState([]);
    const [formData, setFormData] = useState({});

    // Subscriptions (all plans, split into venue / directory in child component)
    const [subscriptionOptions, setSubscriptionOptions] = useState([]);

    // Terms
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Voucher
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherDiscount, setVoucherDiscount] = useState(0);
    const [voucherValid, setVoucherValid] = useState(false);
    const [voucherLabel, setVoucherLabel] = useState('');

    // Payment flow
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentError, setPaymentError] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('');

    // ── Type selector (shown before steps when neither URL param is given) ───
    const [typeSelected, setTypeSelected] = useState(!!forcedType || !!preselectedPlan);
    const [pendingVoucher] = useState(preselectedVoucher);

    // ── Voucher validation ───────────────────────────────────────────────────
    const validateVoucher = useCallback(async (code) => {
        if (!code || code.trim() === '') {
            setVoucherCode('');
            setVoucherDiscount(0);
            setVoucherValid(false);
            setVoucherLabel('');
            return;
        }
        try {
            const res = await fetch(`${baseUrl}/api/Subscription/validate-voucher`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucherCode: code })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.isValid) {
                    setVoucherCode(code);
                    setVoucherDiscount(data.discount);
                    setVoucherValid(true);
                    setVoucherLabel(data.discountLabel);
                    return;
                }
            }
        } catch (e) {
            console.error('Voucher validation error:', e);
        }
        setVoucherCode('');
        setVoucherDiscount(0);
        setVoucherValid(false);
        setVoucherLabel('');
    }, []);

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${baseUrl}/api/Subscription/platform-subscriptions`);
                const data = await res.json();
                const subs = (data.status === 'success' && data.subscriptions) ? data.subscriptions : [];
                setSubscriptionOptions(subs);

                // Determine mode from URL
                let isDir = forcedType === 'pt';
                let preSelected = preselectedPlan;

                if (preselectedPlan && subs.length > 0) {
                    const matched = subs.find(s => s.id?.toLowerCase() === preselectedPlan.toLowerCase());
                    if (matched) {
                        preSelected = matched.id;
                        if (matched.isPtDirectoryPlan) isDir = true;
                    }
                }

                setIsPtDirectoryMode(isDir);

                if (isDir) {
                    setSteps(PT_DIR_STEPS);
                    setFormData({
                        contactEmail: '', contactFirstName: '', contactSurname: '',
                        password: '', confirmPassword: '',
                        country: 'United Kingdom', city: '', postcode: '',
                        businessName: '', services: '',
                        selectedSubscription: preSelected
                    });
                } else {
                    setSteps(VENUE_STEPS);
                    setFormData({
                        shortCodeKey: '', venueName: '', location: '', address: '',
                        contactEmail: '', contactFirstName: '', contactSurname: '',
                        password: '', confirmPassword: '',
                        selectedSubscription: preSelected
                    });
                }

                // Apply voucher from URL
                if (pendingVoucher && !isDir) {
                    await validateVoucher(pendingVoucher);
                }
            } catch (err) {
                setError('Failed to load signup options. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        if (typeSelected) init();
    }, [typeSelected, forcedType, preselectedPlan, pendingVoucher, validateVoucher]);

    // ── When mode is switched from the type selector ──────────────────────────
    const handleSelectType = (ptDir) => {
        setIsPtDirectoryMode(ptDir);
        if (ptDir) {
            setSteps(PT_DIR_STEPS);
            setFormData({
                contactEmail: '', contactFirstName: '', contactSurname: '',
                password: '', confirmPassword: '',
                country: 'United Kingdom', city: '', postcode: '',
                businessName: '', services: '',
                selectedSubscription: ''
            });
        } else {
            setSteps(VENUE_STEPS);
            setFormData({
                shortCodeKey: '', venueName: '', location: '', address: '',
                contactEmail: '', contactFirstName: '', contactSurname: '',
                password: '', confirmPassword: '',
                selectedSubscription: ''
            });
        }
        setCurrentStep(0);
        setTermsAccepted(false);
        setTypeSelected(true);
    };

    const calculateDiscountedPrice = (price) => {
        if (!voucherValid || isPtDirectoryMode) return price;
        return (price * (1 - voucherDiscount)).toFixed(2);
    };

    // ── Validation ───────────────────────────────────────────────────────────
    const validateCurrentStep = () => {
        setError('');
        const stepName = steps[currentStep];

        switch (stepName) {
            case 'Venue Details':   return validateVenueDetails();
            case 'Your Details':    return validatePtDirectoryDetails();
            case 'PT Profile':
                if (!formData.city || formData.city.trim().length < 2) {
                    setError('Please enter your city so clients can find you');
                    return false;
                }
                return true;
            case 'Terms & Conditions':
                if (!termsAccepted) {
                    setError('Please accept the WorldsHighStreet terms and conditions');
                    return false;
                }
                return true;
            case 'Subscription':
                if (!formData.selectedSubscription) {
                    setError('Please select a subscription plan');
                    return false;
                }
                return true;
            default:
                return true;
        }
    };

    const validateVenueDetails = () => {
        if (!formData.shortCodeKey || formData.shortCodeKey.length < 2) {
            setError('Please enter a valid venue key (at least 2 characters)');
            return false;
        }
        if (!formData.venueName) { setError('Please enter your venue name'); return false; }
        if (!formData.contactEmail || !/^[^@]+@[^@]+\.[^@]+$/.test(formData.contactEmail)) {
            setError('Please enter a valid email address'); return false;
        }
        if (!formData.contactFirstName) { setError('Please enter your first name'); return false; }
        if (!formData.contactSurname) { setError('Please enter your surname'); return false; }
        if (!formData.password || formData.password.length < 8) {
            setError('Password must be at least 8 characters'); return false;
        }
        if (formData.password.length > 70) {
            setError('Password must be 70 characters or fewer'); return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match'); return false;
        }
        return true;
    };

    const validatePtDirectoryDetails = () => {
        if (!formData.contactEmail || !/^[^@]+@[^@]+\.[^@]+$/.test(formData.contactEmail)) {
            setError('Please enter a valid email address'); return false;
        }
        if (!formData.contactFirstName) { setError('Please enter your first name'); return false; }
        if (!formData.contactSurname) { setError('Please enter your surname'); return false; }
        if (!formData.password || formData.password.length < 8) {
            setError('Password must be at least 8 characters'); return false;
        }
        if (formData.password.length > 70) {
            setError('Password must be 70 characters or fewer'); return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match'); return false;
        }
        return true;
    };

    // ── Navigation ───────────────────────────────────────────────────────────
    const handleNext = async () => {
        if (!validateCurrentStep()) return;

        if (steps[currentStep + 1] === 'Payment') {
            await initiatePaymentFlow();
            return;
        }
        setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    };

    const handleBack = () => {
        if (currentStep === 0) {
            // Back to type selector
            setTypeSelected(false);
            return;
        }
        setCurrentStep(prev => Math.max(prev - 1, 0));
    };

    // ── Payment ──────────────────────────────────────────────────────────────
    const initiatePaymentFlow = async () => {
        try {
            setPaymentStatus('processing');
            setCurrentStep(prev => prev + 1);

            // Step 1 — create account / venue
            setLoadingMessage(isPtDirectoryMode ? 'Creating your directory account...' : 'Creating your venue...');
            const accountData = await createAccount();

            const userId = accountData.userId || accountData.id;
            if (!userId) throw new Error('Failed to create account – no user ID returned');

            // Step 2 — setup payment
            setLoadingMessage('Setting up payment...');
            let paymentResponse;

            if (isPtDirectoryMode) {
                paymentResponse = await fetch(`${baseUrl}/api/PaymentAdmin/setupdirectorypayment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        UserId: userId,
                        PlanId: formData.selectedSubscription,
                        City: formData.city || null,
                        Postcode: formData.postcode || null,
                        BusinessName: formData.businessName || null,
                        Services: formData.services || null
                    })
                });
            } else {
                const venueId = accountData.venueId;
                if (!venueId) throw new Error('Failed to create venue – missing venue ID');

                paymentResponse = await fetch(`${baseUrl}/api/PaymentAdmin/setupvenuepayment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        VenueId: venueId,
                        SubscriptionId: formData.selectedSubscription,
                        UserId: userId,
                        VoucherCode: voucherValid ? voucherCode : null
                    })
                });
            }

            if (!paymentResponse.ok) {
                const err = await paymentResponse.json().catch(() => ({}));
                throw new Error(err.message || err.Message || 'Failed to setup payment');
            }

            const paymentData = await paymentResponse.json();
            setLoadingMessage('Redirecting to payment provider...');
            const mandateUrl = paymentData.mandateUrl || paymentData.MandateUrl;
            if (mandateUrl) {
                window.location.href = mandateUrl;
            } else {
                throw new Error('Payment URL not received from server');
            }
        } catch (err) {
            console.error('Payment flow error:', err);
            setPaymentStatus('failed');
            setPaymentError(err.message || 'Failed to initiate payment setup');
            setLoadingMessage('');
        }
    };

    const createAccount = async () => {
        const endpoint = isPtDirectoryMode
            ? `${baseUrl}/api/users/pt-directory-signup`
            : `${baseUrl}/api/users/venue-signup`;

        const payload = isPtDirectoryMode
            ? {
                Email: formData.contactEmail,
                Password: formData.password,
                FirstName: formData.contactFirstName,
                Surname: formData.contactSurname
            }
            : {
                ShortCodeKey: formData.shortCodeKey,
                VenueName: formData.venueName,
                Location: formData.location,
                Address: formData.address,
                ContactEmail: formData.contactEmail,
                ContactFirstName: formData.contactFirstName,
                ContactSurname: formData.contactSurname,
                Password: formData.password,
                VenueTermsAccepted: termsAccepted
            };

        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.message || errData.Message || 'Failed to create account');
        }
        return res.json();
    };

    // ── Step renderer ────────────────────────────────────────────────────────
    const renderStepContent = () => {
        const stepName = steps[currentStep];

        switch (stepName) {
            case 'Venue Details':
                return (
                    <VenueDetailsStep
                        formData={formData}
                        setFormData={setFormData}
                        shortCodeValidationText={error}
                        emailValidationText=""
                        passwordValidationText=""
                    />
                );

            case 'Your Details':
                return (
                    <PtDirectoryDetailsStep
                        formData={formData}
                        setFormData={setFormData}
                        emailValidationText={error}
                        passwordValidationText=""
                    />
                );

            case 'PT Profile':
                return <PtDirectoryProfileStep formData={formData} setFormData={setFormData} />;

            case 'Terms & Conditions':
                return (
                    <VenueTermsStep
                        venueTermsAccepted={termsAccepted}
                        setVenueTermsAccepted={setTermsAccepted}
                    />
                );

            case 'Subscription':
                return (
                    <VenueSubscriptionStep
                        subscriptions={subscriptionOptions}
                        selectedSubscription={formData.selectedSubscription}
                        setSelectedSubscription={(id) => setFormData({ ...formData, selectedSubscription: id })}
                        voucherCode={voucherCode}
                        voucherValid={voucherValid}
                        voucherLabel={voucherLabel}
                        calculateDiscountedPrice={calculateDiscountedPrice}
                        validateVoucher={isPtDirectoryMode ? null : validateVoucher}
                        isPtDirectoryMode={isPtDirectoryMode}
                        setIsPtDirectoryMode={isPtDirectoryMode ? null : (val) => {
                            setIsPtDirectoryMode(val);
                            // re-init steps if tab switched
                            setSteps(val ? PT_DIR_STEPS : VENUE_STEPS);
                            setFormData(fd => ({
                                ...fd,
                                selectedSubscription: ''
                            }));
                        }}
                    />
                );

            case 'Payment':
                return (
                    <div>
                        {loadingMessage && (
                            <div className="mb-6 text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                                <p className="text-emerald-400 text-lg font-medium">{loadingMessage}</p>
                            </div>
                        )}
                        <PaymentStep status={paymentStatus} error={paymentError} />
                    </div>
                );

            case 'Complete':
                if (isPtDirectoryMode) {
                    return (
                        <div className="text-center space-y-6 py-4">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/10">
                                <svg className="h-8 w-8 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-luxury-pearl mb-2">You're in the Directory!</h3>
                                <p className="text-luxury-pearl/60">Your PT Directory account is being set up. Check your email to verify your account, then log in to complete your profile.</p>
                            </div>
                            <a href="/login" className="btn-primary inline-flex items-center gap-2 px-8 py-3">
                                Log In to Your Dashboard
                            </a>
                        </div>
                    );
                }
                return <VenueCompletionStep />;

            default:
                return null;
        }
    };

    // ── Type selector screen ──────────────────────────────────────────────────
    if (!typeSelected) {
        return (
            <div className="min-h-screen bg-dark-900 py-8 px-4 flex items-center justify-center">
                <div className="max-w-2xl w-full">
                    <div className="text-center mb-10">
                        <h1 className="text-3xl sm:text-4xl font-display font-bold text-emerald-500 mb-2">
                            Join WorldsHighStreet
                        </h1>
                        <p className="text-emerald-600/70 font-body">
                            How would you like to use WorldsHighStreet?
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                        {/* Venue Admin */}
                        <button
                            onClick={() => handleSelectType(false)}
                            className="card-luxury p-8 text-left hover:border-emerald-500 border-2 border-transparent transition-all duration-200 group"
                        >
                            <div className="flex flex-col items-start gap-4">
                                <div className="p-3 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                                    <BuildingStorefrontIcon className="h-8 w-8 text-emerald-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white mb-2">I run a venue</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        Set up your gym, studio, or personal training business. Manage classes, sessions, clients, and billing — all in one place.
                                    </p>
                                </div>
                                <span className="mt-auto text-emerald-400 font-medium text-sm flex items-center gap-1">
                                    Set up your venue <ArrowRightIcon className="h-4 w-4" />
                                </span>
                            </div>
                        </button>

                        {/* PT Directory */}
                        <button
                            onClick={() => handleSelectType(true)}
                            className="card-luxury p-8 text-left hover:border-amber-500 border-2 border-transparent transition-all duration-200 group"
                        >
                            <div className="flex flex-col items-start gap-4">
                                <div className="p-3 rounded-lg bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                                    <MagnifyingGlassIcon className="h-8 w-8 text-amber-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white mb-2">I'm a PT / trainer</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        Get listed in the WorldsHighStreet PT Directory so clients can find you. Lifetime pricing for early members.
                                    </p>
                                </div>
                                <span className="mt-auto text-amber-400 font-medium text-sm flex items-center gap-1">
                                    Join the directory <ArrowRightIcon className="h-4 w-4" />
                                </span>
                            </div>
                        </button>
                    </div>

                    <p className="text-center text-gray-500 text-sm mt-8">
                        Looking to join a venue as a member?{' '}
                        <a href="/plans" className="text-emerald-400 hover:underline">View venue listings</a>
                    </p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-dark-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-dark-900 py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-3xl sm:text-4xl font-display font-bold text-emerald-500 mb-2">
                        {isPtDirectoryMode ? 'Join the PT Directory' : 'Create Your Venue'}
                    </h1>
                    <p className="text-emerald-600/70 font-body">
                        {isPtDirectoryMode
                            ? 'Get listed in the WorldsHighStreet PT Directory and let clients find you'
                            : 'Set up your personal training business on WorldsHighStreet'}
                    </p>
                </div>

                <div className="mb-8">
                    <AccountSetupStepper currentStep={currentStep} steps={steps} />
                </div>

                <div className="card-luxury p-6 sm:p-8 mb-6">
                    {renderStepContent()}

                    {error && (
                        <div className="mt-4 p-4 glass-luxury rounded-xl border border-performance-pulse/30 bg-gradient-to-br from-performance-pulse/5 to-luxury-obsidian/80">
                            <div className="flex items-start gap-3">
                                <svg className="h-5 w-5 text-performance-pulse flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-sm text-performance-pulse font-body font-medium">{error}</p>
                            </div>
                        </div>
                    )}
                </div>

                {steps[currentStep] !== 'Payment' && steps[currentStep] !== 'Complete' && (
                    <div className="flex justify-between gap-4">
                        <button onClick={handleBack} className="btn-outline flex items-center gap-2">
                            <ArrowLeftIcon className="h-5 w-5" />
                            Back
                        </button>
                        <button onClick={handleNext} className="btn-primary flex items-center gap-2 ml-auto">
                            {steps[currentStep + 1] === 'Payment' ? 'Continue to Payment' : 'Next'}
                            <ArrowRightIcon className="h-5 w-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VenueSignup;







